import { AdvisorClientPicker } from "@/components/layout/AdvisorClientPicker";
import { RegionScopePicker } from "@/components/layout/RegionScopePicker";

import type { ModuleSection, RecordScope } from "@/lib/module-registry";

/**
 * The scope picker for a record-scoped section, rendered in the sidebar
 * header beneath the module switcher.
 *
 * Its own component for the same Rules-of-Hooks reason as
 * `RecordScopeSection`: `recordScope.useScope` is a hook, and `ModuleSidebar`
 * renders whichever module is active — calling it in a loop over
 * `mod.sections` would change the hook count when the user switches modules.
 * One component per section pins each call to a fixed position.
 *
 * The chrome is shared (`ScopePicker`); `recordScope.picker` chooses what is
 * being picked. Both branches call exactly one hook, so the hook count is
 * stable whichever renders.
 */
export function RecordScopePicker({
  section,
  onClose,
}: {
  /**
   * A section carrying `recordScope`. The intersection makes the invariant
   * structural rather than a convention: `useScope` below is then an
   * unconditional call by construction, and a caller passing a non-scoped
   * section is a type error instead of a runtime "Rendered fewer hooks than
   * expected" that unmounts the sidebar.
   */
  section: ModuleSection & { recordScope: RecordScope };
  /** Closes the mobile drawer after a selection. */
  onClose: () => void;
}) {
  const scope = section.recordScope;
  const selected = scope.useScope();

  if (scope.picker === "region" || scope.picker === "gas-region") {
    return (
      <RegionScopePicker
        commodity={scope.picker === "gas-region" ? "gas" : "power"}
        selected={selected}
        placeholder={scope.pickerPlaceholder}
        onClose={onClose}
      />
    );
  }

  return (
    <AdvisorClientPicker
      selected={selected}
      basePath={scope.basePath}
      placeholder={scope.pickerPlaceholder}
      onClose={onClose}
    />
  );
}
